import { useState } from 'react';
import useAsync from '../hooks/useAsync';
import { useLookups } from '../context/LookupsContext';
import { expensesApi, authApi, usersApi } from '../api';
import he from '../i18n/he';
import Modal from './Modal';
import { TextInput, NumberInput, Select } from './Form';
import { ErrorText } from './Loader';
import PayerSplitField from './PayerSplitField';
import { initialPayerState, payersPayload, splitAddsUp } from '../utils/payers';
import ExpenseChargeFields, { useChargeState } from './ExpenseChargeFields';
import { ChargeActDialog } from './ExpenseChargeExisting';
import { useReloadAlerts } from '../context/AlertsContext';

// Create/edit modal for an expense. `jobId` present → a job expense; omitted → an
// office (job-less) expense. `context` ('job' | 'office') scopes the category list.
//
// A JOB EXPENSE ANSWERS TWO QUESTIONS, and the form keeps them apart because they are
// genuinely separate facts: WHO PAID (our money, out or not yet) and WHO BEARS IT (we
// absorb it, or the client is charged). An office expense answers only the first — it
// hangs off no job and therefore off no client, so there is nobody to charge.
//
// THE BEARER HAS NO DEFAULT, on purpose. Migration 052's boolean defaulted to "not
// charged", which is why every row in the table means "nobody was asked" rather than
// "we absorb this" — and why a report of what a client costs us could not be written
// honestly. Save stays disabled until the choice is made, so no new row can be born
// unstated. Editing a row that predates the question asks it once, here.
// Which of the three states a stored row is in. 'fronted' is the CONJUNCTION — unpaid
// and naming a fronter — because the column survives reimbursement as a record of who
// actually went to the shop; reading it alone would reopen a settled expense as if
// nobody had been paid back.
const initialPaidState = (expense) => {
  const unpaid = expense?.is_paid === 0 || expense?.is_paid === false;
  if (unpaid && expense?.incurred_by_user_id) return 'fronted';
  return unpaid ? 'unpaid' : 'paid';
};

// THE PAYMENT STATE HAS THREE VALUES, NOT TWO, and the third is a genuinely different
// fact rather than a flavour of "unpaid". A cost somebody paid for out of their own
// pocket — the secretary buying cakes for a client meeting — has not been paid BY THE
// BUSINESS: no partner's money has left, so it stays out of the standing gap exactly as
// any unpaid cost does, and counts in full in profit exactly as any cost does. What is
// different is that a person is out of pocket, and that is what the third state records.
//
// It is a separate option rather than a checkbox beside "unpaid" because choosing it
// carries an obligation — a name, and a debt the business owes them. See migration 061.
// `owner` ({ title, clientName }) names the job in the charge dialogs a charged cost opens.
export default function ExpenseModal({ jobId, context = 'job', expense, owner, onClose, onSaved }) {
  const { data: partners } = useAsync(() => authApi.partners());
  // WHO MAY BE NAMED AS THE FRONTER, and it is deliberately not the list above. Partners
  // are excluded by the server (users/service.js listFronters): a partner who pays out of
  // their own pocket records a PAID expense with themselves as payer, which is precisely
  // what the standing gap already settles. Offering one here would produce the wrong
  // record and quietly drop their reimbursement out of the balance.
  const { data: fronters } = useAsync(() => usersApi.fronters());
  const lookups = useLookups();
  const categoryOptions = lookups.categoryOptionsFor(context, expense?.category);
  const isNew = !expense?.id;
  const [form, setForm] = useState({
    category: expense?.category || (categoryOptions[0] && categoryOptions[0].value) || '',
    description: expense?.description || '',
    amount: expense?.amount ?? '',
    assessed_value: expense?.assessed_value ?? '',
    // A <select> value is a string, so the flag is carried as 'paid' | 'unpaid' and
    // converted on submit. A new expense defaults to paid — that is still the ordinary
    // case, and it keeps the modal behaving as it did for everything else.
    // Three states now. A stored row is 'fronted' when it is unpaid AND names who put
    // their own money in — the pair, not either half, so a reimbursed cost reopens as
    // plain 'paid' and keeps its fronter on the row rather than reverting the state.
    paid_state: initialPaidState(expense),
    incurred_by_user_id: expense?.incurred_by_user_id || '',
    // '' is the unchosen state for a new row AND for a legacy one stored as NULL. Both
    // block Save until answered, which is the same thing said twice rather than two
    // different rules.
    borne_by: expense?.borne_by || '',
    charge_amount: expense?.charge_amount ?? '',
    vendor: expense?.vendor || '',
    // The date the cost was incurred. New expenses default to today; an existing
    // row keeps its stored date (older rows saved before the field existed have
    // none, so they fall back to today too) and stays editable either way.
  });
  // WHO PAID — one partner or both with their parts (069). Its own state, because the
  // shared field owns its shape; see components/PayerSplitField.
  const [payerState, setPayerState] = useState(() => initialPayerState(expense));
  const [error, setError] = useState(null);
  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });
  // A priced client-borne cost is a charge the moment it is saved; the first one on a
  // topic converts it, and that has to be acknowledged. See ExpenseChargeFields.
  // THE ROW'S CHARGE STATE, apart from the form. Correcting or cancelling the charge in
  // place re-reads it (GET /expenses/:id) while the form — whatever has been typed and not
  // yet saved — stays exactly as it is. `chargeChanged` remembers that something was
  // written, so closing the dialog still reloads the screen behind it.
  const [current, setCurrent] = useState(expense);
  const [chargeChanged, setChargeChanged] = useState(false);
  const charge = useChargeState({ jobId, expense: current, form });
  const [acknowledged, setAcknowledged] = useState(false);
  // 'correct' | 'cancel' while the charge's own dialog is open in place of this one.
  const [chargeAct, setChargeAct] = useState(null);
  const reloadAlerts = useReloadAlerts();

  const fronterOptions = [
    { value: '', label: he.common.choose },
    ...(fronters || []).map((u) => ({ value: u.id, label: u.name })),
  ];
  const paidOptions = [
    { value: 'paid', label: he.expenses.paid },
    { value: 'unpaid', label: he.expenses.unpaid },
    { value: 'fronted', label: he.expenses.fronted },
  ];
  const isGoods = form.category === 'goods';
  const isPaid = form.paid_state === 'paid';
  // Fronted is a kind of NOT-paid: no partner money has left, so the expense is stored
  // with is_paid = 0 and no payer, exactly as a plain unpaid one is. The only difference
  // on the wire is the name of who is owed for it.
  const isFronted = form.paid_state === 'fronted';
  // The bearer question belongs to a job expense alone — an office cost has no client.
  const isJobExpense = jobId != null;
  // An unanswered cost blocks Save exactly as an unanswered bearer does, and for the
  // same reason: the form has TWO money boxes now — what it cost us and what we charge
  // the client — and filling only the second is an easy slip that used to save silently
  // as a cost of zero. A typed 0 is a real answer and passes.
  const costMissing = form.amount === '' || !Number.isFinite(Number(form.amount));
  const chargedOn = form.borne_by === 'client';
  const borneOptions = [
    { value: '', label: he.common.choose },
    { value: 'us', label: he.expenses.borneUs },
    { value: 'client', label: he.expenses.borneClient },
  ];

  const submit = async () => {
    try {
      const payload = {
        job_id: jobId ?? null,
        category: form.category,
        description: form.description,
        // EMPTY IS MISSING, NOT ZERO. This used to be `Number(form.amount) || 0`, which
        // turned a blank box into a cost of nothing and made the server's
        // `amount_required` guard unreachable — every expense arrived with a number. A
        // typed 0 still goes through as 0, for goods received free.
        amount: form.amount === '' ? null : Number(form.amount),
        assessed_value: isGoods && form.assessed_value !== '' ? Number(form.assessed_value) : null,
        is_paid: isPaid,
        // No payer on an unpaid expense — the server stores none either way, and sending
        // one would only look like a claim that they paid it. Paid: one partner for the
        // whole amount, or both with parts that add up to it (069).
        ...(isPaid ? payersPayload(payerState, partners || []) : {}),
        // WHO FRONTED IT. Sent only in the fronted state — in the other two nobody is
        // out of pocket waiting, and a name here would read as a debt that does not
        // exist. The server validates it against USERS (never partners), which is what
        // lets it be the secretary, and reads it into no money figure at all.
        incurred_by_user_id: isFronted ? Number(form.incurred_by_user_id) || null : null,
        // Sent only for a job expense; the server stores null for an office one either
        // way, and claiming a bearer for a cost with no client would be a claim about
        // nobody.
        borne_by: isJobExpense ? form.borne_by : null,
        // Only meaningful on a charged-on cost, and optional even there — the price may
        // not be agreed yet. The server clears it whenever the bearer is not the client.
        charge_amount: chargedOn && form.charge_amount !== '' ? Number(form.charge_amount) : null,
        // Sent only when this save converts a topic, and only once ticked — the server
        // refuses the conversion without it.
        convert_acknowledged: charge.converts && acknowledged,
        vendor: form.vendor,
      };
      if (isNew) await expensesApi.create(payload);
      else await expensesApi.update(expense.id, payload);
      // Any save can change the money dot's amber: a client-borne cost with no price yet is
      // a charge waiting to be raised, and a price or a change of bearer clears one. So
      // the sidebar re-reads now rather than on the next full load.
      reloadAlerts();
      onSaved();
    } catch (e) {
      setError(e);
    }
  };

  // A charged cost's charge is corrected or cancelled in the money screen's own dialog,
  // shown INSTEAD of this one — one dialog at a time — and only that dialog closes. This
  // one comes back with the edits intact and the charge as it now stands.
  //
  // A CANCEL ALSO EMPTIES THE PRICE BOX. The released row still carries its old price, and
  // a priced client-borne cost is charged on save — so leaving it would make the next
  // Save quietly raise the charge that was just cancelled. Empty, the cost saves pending
  // ("טרם נקבע"); typing a price back is a deliberate act, and the form says it charges.
  const afterChargeAct = async () => {
    const act = chargeAct;
    reloadAlerts();
    setChargeChanged(true);
    try {
      const fresh = await expensesApi.get(expense.id);
      setCurrent((c) => ({ ...c, ...fresh }));
    } catch (e) {
      setError(e);
    }
    if (act === 'cancel') setForm((f) => ({ ...f, charge_amount: '' }));
    setChargeAct(null);
  };
  const close = () => (chargeChanged ? onSaved() : onClose());

  if (chargeAct) {
    return (
      <ChargeActDialog
        act={chargeAct}
        expense={current}
        owner={owner}
        onClose={() => setChargeAct(null)}
        onSaved={afterChargeAct}
      />
    );
  }

  return (
    <Modal
      title={isNew ? he.jobs.addExpense : he.jobs.editExpense}
      onClose={close}
      onSave={submit}
      saveDisabled={costMissing || (isJobExpense && !form.borne_by) || (isFronted && !form.incurred_by_user_id)
        || (isPaid && !splitAddsUp(payerState, form.amount, partners || []))
        || charge.previewPending || (charge.converts && !acknowledged)}
    >
      <Select label={he.jobs.category} value={form.category} onChange={set('category')} options={categoryOptions} />
      <TextInput label={he.jobs.description} value={form.description} onChange={set('description')} />
      <NumberInput label={he.jobs.amount} value={form.amount} onChange={set('amount')} />
      {/* Says WHY Save is dark, at the field that is blocking it. A disabled button with
          no explanation is how a form teaches people to distrust it. */}
      {costMissing && <p className="field-error">{he.expenses.costRequired}</p>}
      {isGoods && (
        <NumberInput label={he.jobs.assessedValue} value={form.assessed_value} onChange={set('assessed_value')} />
      )}
      {/* WHO BEARS IT — directly under what we paid, because the two numbers belong to
          one another: `amount` is what left us, `charge_amount` is what comes back. */}
      {isJobExpense && (
        <>
          {/* Locked once the charge exists: turning it back to "we absorb it" would leave
              that invoice as revenue with nothing behind it. */}
          <Select
            label={he.expenses.borneBy}
            value={form.borne_by}
            onChange={set('borne_by')}
            options={borneOptions}
            disabled={charge.charged}
          />
          {chargedOn && (
            <ExpenseChargeFields
              form={form}
              set={set}
              state={charge}
              acknowledged={acknowledged}
              onAcknowledge={setAcknowledged}
              expense={current}
              onChargeAct={setChargeAct}
            />
          )}
        </>
      )}
      {/* The payer appears only once the expense is paid: it records who actually paid,
          not who is expected to. */}
      <Select label={he.expenses.paymentState} value={form.paid_state} onChange={set('paid_state')} options={paidOptions} />
      {isPaid && (
        <PayerSplitField
          label={he.jobs.payer}
          partners={partners}
          amount={form.amount}
          state={payerState}
          onChange={setPayerState}
        />
      )}
      {/* The fronter is asked for exactly where the payer would be, because it answers
          the same position in the form — whose money is in this cost — with the one
          answer the payer field structurally cannot give: somebody who is not a partner.
          Save is blocked until a name is chosen, for the same reason the bearer blocks
          it: "somebody is owed for this" with no name is not a record of anything. */}
      {isFronted && (
        <>
          <Select
            label={he.expenses.frontedBy}
            value={form.incurred_by_user_id}
            onChange={set('incurred_by_user_id')}
            options={fronterOptions}
          />
          {!form.incurred_by_user_id && <p className="field-error">{he.expenses.frontedRequired}</p>}
        </>
      )}
      <TextInput label={he.jobs.vendor} value={form.vendor} onChange={set('vendor')} />
      {error && <ErrorText error={error} />}
    </Modal>
  );
}
